<script>
import { preventDefault } from "svelte/legacy";
import { fade } from "svelte/transition";
import { modalState, hideModal } from "@app/store/modalStore.svelte.js";
import { eventGroupState, isEventGroupLocked } from "@app/store/eventGroupStore.svelte.js";
import { insertHistoryAction } from "@app/store/historyStore.svelte.js";
import { projectState } from "@app/store/projectStore.svelte.js";
import { toast } from "@zerodevx/svelte-toast";
import * as m from "@app/paraglide/messages.js";

let srcGroupId = $state("");
let trgGroupId = $state("");

$effect(() => {
    if (modalState.selected !== "copyEventText") return;

    const selectedIndex =
        typeof projectState.selected === "number" ? projectState.selected : 0;
    const targetGroup = eventGroupState[selectedIndex];
    const sourceIndex = selectedIndex === 0 ? 1 : 0;
    const sourceGroup = eventGroupState[sourceIndex];

    if (targetGroup?.id) {
        trgGroupId = targetGroup.id;
    }

    if (sourceGroup?.id) {
        srcGroupId = sourceGroup.id;
    }
});

function getOverlapDuration(a, b) {
    const overlapStart = Math.max(a.start, b.start);
    const overlapEnd = Math.min(a.end, b.end);
    return Math.max(0, overlapEnd - overlapStart);
}

function findBestTargetIndex(sourceEvent, targetEvents) {
    let bestIndex = null;
    let bestDuration = 0;
    let bestStart = Infinity;

    targetEvents.forEach((targetEvent, index) => {
        const duration = getOverlapDuration(sourceEvent, targetEvent);
        if (
            duration > bestDuration ||
            (duration === bestDuration &&
                duration > 0 &&
                targetEvent.start < bestStart)
        ) {
            bestIndex = index;
            bestDuration = duration;
            bestStart = targetEvent.start;
        }
    });

    return bestDuration > 0 ? bestIndex : null;
}

function deduplicateArray(arr) {
    if (!arr || !arr.length) return [];

    const valueMap = new Map();

    return arr.filter((item) => {
        if (item === null || item === undefined) return false;

        const normalizedItem = String(item).trim().toLowerCase();

        if (!valueMap.has(normalizedItem)) {
            valueMap.set(normalizedItem, true);
            return true;
        }

        return false;
    });
}

function copyEventTextFromGroup() {
    try {
        const srcIndex = eventGroupState.findIndex(
            (eventGroup) => eventGroup.id === srcGroupId,
        );
        const trgIndex = eventGroupState.findIndex(
            (eventGroup) => eventGroup.id === trgGroupId,
        );

        if (srcIndex === -1 || trgIndex === -1) {
            throw new Error(m.copy_event_text_failed());
        }

        if (srcIndex === trgIndex) {
            throw new Error(m.source_target_match_error());
        }

        if (isEventGroupLocked(trgIndex)) {
            throw new Error(m.event_group_locked_toast());
        }

        const sourceEvents = [...eventGroupState[srcIndex].events].sort(
            (a, b) => a.start - b.start,
        );
        const targetEvents = eventGroupState[trgIndex].events;
        const modifiedTargetIndices = new Set();

        sourceEvents.forEach((sourceEvent) => {
            const targetIndex = findBestTargetIndex(sourceEvent, targetEvents);

            if (targetIndex === null) return;

            const targetEvent = targetEvents[targetIndex];
            targetEvent.text = (targetEvent.text || "") + (sourceEvent.text || "");

            if (sourceEvent.speakers?.length) {
                targetEvent.speakers = [
                    ...(targetEvent.speakers || []),
                    ...sourceEvent.speakers,
                ];
            }

            if (sourceEvent.recipients?.length) {
                targetEvent.recipients = [
                    ...(targetEvent.recipients || []),
                    ...sourceEvent.recipients,
                ];
            }

            if (sourceEvent.tags?.length) {
                targetEvent.tags = [
                    ...(targetEvent.tags || []),
                    ...sourceEvent.tags,
                ];
            }

            modifiedTargetIndices.add(targetIndex);
        });

        modifiedTargetIndices.forEach((index) => {
            const event = targetEvents[index];
            event.speakers = deduplicateArray(event.speakers);
            event.recipients = deduplicateArray(event.recipients);
            event.tags = deduplicateArray(event.tags);
        });

        insertHistoryAction({
            name: "copy event text",
            eventGroup: trgIndex,
            snapshots: [
                {
                    store: "eventGroupState",
                    value: JSON.stringify(eventGroupState),
                },
            ],
        });

        toast.push(m.copy_event_text_success(), {
            classes: ["toast-success"],
        });
    } catch (err) {
        console.log(err, err.message);

        toast.push(m.copy_event_text_failed(), {
            classes: ["toast-danger"],
        });
    } finally {
        hideModal();
    }
}
</script>

<div
    transition:fade={{ duration: 100 }}
    class="modal {modalState.selected === 'copyEventText' ? 'show d-block' : ''}"
    role="dialog"
    aria-labelledby="copyEventTextModalTitle"
    aria-describedby="copyEventTextModalDesc"
    tabindex="-1"
    id="CopyEventTextModal">
    <div class="modal-dialog modal-dialog-centered" role="document">
        <div class="modal-content">
            <div class="modal-header">
                <h4 class="modal-title" id="copyEventTextModalTitle">
                    {m.copy_event_text()}
                </h4>
                <button
                    type="button"
                    class="btn-close"
                    id="closeCopyEventTextBtn"
                    aria-label={m.close_dialog()}
                    onclick={hideModal}></button>
            </div>
            <div class="modal-body">
                <p id="copyEventTextModalDesc" class="visually-hidden">
                    {m.copy_event_text_description()}
                </p>
                <form onsubmit={preventDefault(copyEventTextFromGroup)}>
                    <div class="mb-3">
                        <label class="form-label" for="copyEventTextSourceSelect"
                            >{m.source_group()}</label>
                        <select
                            class="form-select"
                            id="copyEventTextSourceSelect"
                            bind:value={srcGroupId}
                            aria-describedby="copyEventTextSourceHelp">
                            {#each eventGroupState as eventGroupOption (eventGroupOption.id)}
                                <option value={eventGroupOption.id}
                                    >{eventGroupOption.name}</option>
                            {/each}
                        </select>
                        <div id="copyEventTextSourceHelp" class="form-text">
                            {m.copy_event_text_source_help()}
                        </div>
                    </div>
                    <div class="mb-3">
                        <label class="form-label" for="copyEventTextTargetSelect"
                            >{m.target_group()}</label>
                        <select
                            class="form-select"
                            id="copyEventTextTargetSelect"
                            bind:value={trgGroupId}
                            aria-describedby="copyEventTextTargetHelp">
                            {#each eventGroupState as eventGroupOption (eventGroupOption.id)}
                                <option value={eventGroupOption.id}
                                    >{eventGroupOption.name}</option>
                            {/each}
                        </select>
                        <div id="copyEventTextTargetHelp" class="form-text">
                            {m.copy_event_text_target_help()}
                        </div>
                    </div>
                    <button
                        class="btn btn-primary float-end"
                        id="copyEventTextBtn"
                        type="button"
                        onclick={copyEventTextFromGroup}
                        aria-label={m.copy_event_text()}>{m.copy()}</button>
                </form>
            </div>
        </div>
    </div>
</div>
